<template>
  <div class="w100p">
    <v-progress-circular
      v-if="isLoading"
      indeterminate
      color="primary"
    ></v-progress-circular>
    <canvas ref="RenderCanvas" class="w100p" :class="contentClass" />
  </div>
</template>

<script>
import * as BABYLON from '@babylonjs/core';
import '@babylonjs/loaders/glTF';

export default {
  props: {
    needParse: {
      type: Boolean,
      default: false
    },
    model: {
      type: [File, String, Object, Blob],
      default: null
    },
    contentClass: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      isLoading: false,
      canvas: null,
      scene: null,
      engine: null
    };
  },

  watch: {
    model(v) {
      this.scene = null;
      this.$nextTick(async () => {
        if (v) this.scene = await this.createScene();
        this.engine.resize();
      });
    }
  },

  mounted() {
    this.init();
  },

  beforeDestroy() {
    this.engine.dispose();
    this.engine = null;
    this.scene = null;
  },

  methods: {
    async init() {
      // Get the canvas DOM element
      this.canvas = this.$refs.RenderCanvas;
      const canvas = this.canvas;
      // Load the 3D engine
      this.engine = new BABYLON.Engine(canvas, true, {
        preserveDrawingBuffer: false,
        stencil: true
      });
      const engine = this.engine;

      // call the createScene function
      this.scene = await this.createScene(engine, canvas);

      this.render();
      this.$nextTick(() => engine.resize());
    },

    async createScene(engine = this.engine, canvas = this.canvas) {
      // CreateScene function that creates and return the scene

      // Create a basic BJS Scene object
      const scene = new BABYLON.Scene(engine, {});
      scene.clearColor = new BABYLON.Color4(0, 0, 0, 0);
      const camera = new BABYLON.FreeCamera(
        'camera1',
        new BABYLON.Vector3(0, 5, -10),
        scene
      );
      // Target the camera to scene origin
      camera.setTarget(BABYLON.Vector3.Zero());
      // Attach the camera to the canvas
      camera.attachControl(canvas, true);
      this.isLoading = true;
      const { result, error } = await this.parseModel(this.model);
      if (error) {
        this.throwError(error);
        return;
      }

      BABYLON.SceneLoader.Append(
        '',
        result,
        scene,
        (appendScene) => {
          const root = appendScene.meshes[0];
          scene.createDefaultCameraOrLight(true, true, true);
          scene.activeCamera.useAutoRotationBehavior = true;
          scene.activeCamera.alpha -= Math.PI;
          scene.activeCamera.beta -= Math.PI / 8;
          scene.activeCamera.upperBetaLimit = 1.5;

          const light = new BABYLON.DirectionalLight(
            'light1',
            new BABYLON.Vector3(-2, -3, 1),
            scene
          );
          light.intensity = 1;
          light.position = new BABYLON.Vector3(6, 9, 3);

          const generator = new BABYLON.ShadowGenerator(512, light);
          generator.useBlurExponentialShadowMap = true;
          generator.blurKernel = 32;
          for (let i = 0; i < scene.meshes.length; i++) {
            generator.addShadowCaster(scene.meshes[i]);
          }
          const helper = scene.createDefaultEnvironment({
            enableGroundMirror: true,
            groundShadowLevel: 1,
            sizeAuto: true
          });
          helper.setMainColor(new BABYLON.Color4(255, 255, 255, 0));
          scene.activeCamera.upperRadiusLimit = scene.activeCamera.radius + 5;

          this.isLoading = false;
        },
        (progress) => {
          this.isLoading = true;
        },
        (scene, message, error) => {
          this.throwError(error);
        }
      );

      // Return the created scene
      return scene;
    },

    parseModel(model) {
      return new Promise((resolve, reject) => {
        if (this.needParse) {
          (async () => {
            const response = await fetch(model);
            const responseClone = response.clone();
            try {
              // test if the file is a glTF file
              const obj = await responseClone.json();
              // if it is, return origin url
              resolve({ result: model, error: null });
            } catch (error) {
              // if is not glTF, might be glb, parse to blob and return
              const blob = await response.blob();
              const FR = new FileReader();

              FR.onload = (e) => {
                resolve({ result: FR.result, error: null });
              };
              FR.onerror = (e) => {
                resolve({ error: e });
              };

              FR.readAsDataURL(blob);
            }
          })();
        } else {
          // if not need parse, return origin url
          resolve({ result: model });
        }
      });
    },

    throwError(error) {
      this.$toast.error(
        this.$t('ARTWORK.EDIT.TOAST.ALERT.UNABLE_TO_LOAD_MODEL')
      );
      this.$emit('error');
      this.isLoading = false;
      this.engine.dispose();
      console.error(error);
    },

    resize() {
      if (!this.engine) {
        window.removeEventListener('resize', this.resize);
        return;
      }
      this?.engine?.resize();
    },

    render(scene = this.scene, engine = this.engine) {
      // run the render loop
      engine?.runRenderLoop(
        () => scene && scene.activeCamera && scene.render()
      );
      // the canvas/window resize event handler
      window.addEventListener('resize', this.resize);
    }
  }
};
</script>

<style lang="scss" scoped>
//
</style>